import { Link } from "react-router-dom";
import {
  MapPin, Zap, Sparkles, Heart, Gift, Music, Check, ArrowRight,
  Users, Store, LayoutGrid, CalendarDays, Target, Radio,
} from "lucide-react";

const userBullets = [
  { icon: LayoutGrid, text: "Le serate in un unico posto" },
  { icon: MapPin, text: "Mappa con gli eventi vicino a te" },
  { icon: Sparkles, text: "Serate alternative alle discoteche" },
  { icon: Gift, text: "Eventi gratuiti e promo" },
  { icon: Heart, text: "Salva e condividi le serate" },
  { icon: Zap, text: "Gratis, senza obbligo di registrazione" },
];

const userText = [
  "WYD raccoglie le serate alternative alle discoteche: DJ set piccoli, aperitivi, promo, eventi gratuiti, serate chill, bar con musica.",
  "Filtra per categoria, quartiere o data, oppure premi \"Stasera\" per vedere solo cosa succede oggi.",
  "Sulla mappa trovi gli eventi intorno a te, con l'indirizzo del locale quando è disponibile.",
  "Con un account gratuito salvi le serate che ti interessano; ogni evento si condivide con un link su WhatsApp e Instagram.",
  "Ogni evento viene controllato dal nostro team prima di essere pubblicato.",
];

const barBullets = [
  { icon: Check, text: "Gratis, senza registrazione" },
  { icon: Radio, text: "Segnalazione in pochi secondi con un link" },
  { icon: CalendarDays, text: "Pagina dedicata a ogni evento" },
  { icon: MapPin, text: "Il tuo locale sulla mappa" },
  { icon: Music, text: "Categorie pensate per i bar" },
  { icon: Target, text: "Un pubblico che cerca serate alternative" },
];

const barText = [
  "WYD è dedicato alle serate dei bar: niente discoteche, solo aperitivi, DJ set, promo, musica live ed eventi tranquilli.",
  "Proporre un evento è gratuito e non serve registrarsi: basta il nome del locale e il link al post o alla pagina dell'evento.",
  "Dopo il nostro controllo l'evento ottiene una pagina dedicata, compare nel catalogo e sulla mappa, e si può condividere con un link.",
  "Le tue serate restano consultabili su WYD fino alla data dell'evento, invece di sparire in 24 ore come una storia.",
  "WYD parla solo di serate alternative: chi lo usa sta cercando proprio eventi come i tuoi.",
];

export default function Why() {
  return (
    <div className="pt-28 pb-16" data-testid="why-page">
      {/* Intro */}
      <div className="max-w-7xl mx-auto px-5 mb-16">
        <div className="overline text-wyd-accent mb-3">Perché WYD</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
          La nightlife alternativa di Milano, <span className="text-gradient">finalmente chiara</span>.
        </h1>
        <p className="mt-5 text-lg text-wyd-muted max-w-2xl">
          Una piattaforma pensata per chi vive la notte e per i locali che la animano. Ecco cosa cambia con WYD.
        </p>
      </div>

      {/* Sezione Utenti */}
      <section data-testid="why-users-section" className="max-w-7xl mx-auto px-5">
        <div className="rounded-3xl border border-white/10 bg-wyd-surface overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 sm:p-12">
              <div className="inline-flex items-center gap-2 overline text-wyd-bg bg-wyd-accent px-3 py-1 rounded-full">
                <Users size={14} /> Per gli utenti
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl mt-5">Perché usare WYD</h2>
              <p className="text-xl text-wyd-accent font-medium mt-2">La notte, finalmente organizzata.</p>
              <div className="mt-6 space-y-4">
                {userText.map((t) => (
                  <p key={t} className="text-wyd-muted leading-relaxed">{t}</p>
                ))}
              </div>
              <Link to="/mappa" data-testid="why-users-cta"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-wyd-accent text-wyd-bg font-semibold hover:glow-magenta transition-shadow duration-300">
                Esplora la mappa e scopri dove uscire stasera <ArrowRight size={18} />
              </Link>
            </div>
            <div className="p-8 sm:p-12 bg-wyd-bg border-t lg:border-t-0 lg:border-l border-white/10">
              <div className="overline text-wyd-muted mb-5">In sintesi</div>
              <ul className="space-y-4">
                {userBullets.map(({ icon: Icon, text }, i) => (
                  <li key={text} className="flex items-center gap-4 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                    <span className="w-11 h-11 shrink-0 rounded-xl grid place-items-center bg-wyd-accent/15 text-wyd-accent">
                      <Icon size={20} />
                    </span>
                    <span className="text-white font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sezione Bar */}
      <section data-testid="why-bars-section" className="max-w-7xl mx-auto px-5 mt-10">
        <div className="rounded-3xl border border-white/10 bg-wyd-surface overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 sm:p-12 bg-wyd-bg order-2 lg:order-1 border-t lg:border-t-0 lg:border-r border-white/10">
              <div className="overline text-wyd-muted mb-5">Cosa offriamo ai locali</div>
              <ul className="space-y-4">
                {barBullets.map(({ icon: Icon, text }, i) => (
                  <li key={text} className="flex items-center gap-4 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                    <span className="w-11 h-11 shrink-0 rounded-xl grid place-items-center bg-wyd-accent/20 text-wyd-accent">
                      <Icon size={20} />
                    </span>
                    <span className="text-white font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 sm:p-12 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 overline text-wyd-bg bg-wyd-accent px-3 py-1 rounded-full">
                <Store size={14} /> Per i bar
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl mt-5">Perché essere su WYD</h2>
              <p className="text-xl text-wyd-accent font-medium mt-2">Più visibilità per le tue serate, senza costi.</p>
              <div className="mt-6 space-y-4">
                {barText.map((t) => (
                  <p key={t} className="text-wyd-muted leading-relaxed">{t}</p>
                ))}
              </div>
              <Link to="/proponi" data-testid="why-bars-cta"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-wyd-accent text-wyd-bg font-semibold hover:glow-violet transition-shadow duration-300">
                Proponi il tuo evento <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
